import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {Grid} from "antd";

import { authActions } from "../../store";
import { fetchWrapper } from "../../helpers";
import { useOrganization } from "../../context/organizationContext";
import { ImeanHeader } from "../../components/Header/Header";
import AppNavigation from "../../components/AppNavigation";

// icons (reuse existing assets)
import calendarIcon from "./icons/Calendar.png";
import profileIcon from "./icons/Analytics.png";
import contentPlanIcon from "./icons/Content Plan.png";
import InstantPostIcon from "./icons/InstantPost.png";
import AssistentIcon from "./icons/Assistant.png";
import ModerationIcon from "./icons/Moderation.png";

const baseUrl = `${process.env.REACT_APP_API_URL}`;
const { useBreakpoint } = Grid;

export default function NewHomePage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const org = useOrganization();
  const user = useSelector((s) => s.auth.user);

  const [, setLocalUser] = useState(() => JSON.parse(localStorage.getItem("user") || "{}"));

  useEffect(() => {
    fetchWrapper.get(`${baseUrl}/auth/refresh-token`).then((res) => {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      const next = { ...stored, ...res };
      localStorage.setItem("user", JSON.stringify(next));
      setLocalUser(next);
      dispatch(authActions.updateUser());
    }).catch(() => {});
  }, [dispatch]);

  const roleBadge = useMemo(() => t("home.role") || "Ambassador", [t]);
  const firstName = useMemo(() => (user?.name || "").split(" ")[0] || t("home.friend") || "Friend", [user, t]);

  const screens = useBreakpoint();
  const isMobile = !(!screens.md && !screens.lg && !screens.xl);

  return (
    <div className="relative w-full min-h-[100vh] bg-gray-50 text-gray-900 overflow-hidden">
      {/* Background decor */}
      <BackgroundDecor />
      <div className="relative z-10 flex flex-col">
        {/* Новый глобальный Header */}
        <ImeanHeader initialDark={true} />
        <main id="main" role="main" className="flex-1 container mx-auto px-0 md:px-2">
          {/* Hero */}
          <section className="px-4 pt-10 pb-8">
            <div className="max-w-6xl mx-auto text-center">
              <span className="inline-block rounded-full bg-gray-200 text-gray-700 text-xs px-3 py-1 select-none">{roleBadge}</span>
              <h1 className="mt-4 font-semibold tracking-tight text-[clamp(28px,5vw,44px)]">
                {t("home.welcome") || "Welcome"}, {firstName}
              </h1>
              <p className="mt-2 text-gray-500 text-[clamp(12px,2vw,14px)]">{t("home.useYourInnerVoice") || "Use your inner voice"}</p>
            </div>
          </section>

          {/* Features panel */}
          <section className="px-4 pb-0">
            <div className="max-w-6xl mx-auto relative rounded-2xl border border-gray-200 bg-white shadow-sm p-5 sm:p-8 md:p-10">
              <div className="features-grid grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10">
                {/* 1. Calendar — первый */}
                <FeatureTile icon={calendarIcon} title={t("home.publicationCalendar") || "Publication Calendar"} text={isMobile ? (t("home.publicationCalendarDesc") || "Plan and track your posts effortlessly with a clean calendar.") : null} to="/publication-calendar" iconSize="sm" />
                {/* 2. Instant Post */}
                <FeatureTile icon={InstantPostIcon} title={t("home.instantPost") || "Instant Post"} text={isMobile ? (t("home.instantPostDesc") || "Stay connected anytime with reliable infrastructure for uninterrupted communication.") : null} to="/posts" iconSize="sm" />
                {/* 3. Content Plan */}
                <FeatureTile icon={contentPlanIcon} title={t("home.contentPlan") || "Content Plan"} text={isMobile ? (t("home.contentPlanLong") || "Plan your content effectively and transform the way you work.") : null} to="/content-plan" iconSize="sm" />
                {/* 4. Assistant */}
                <FeatureTile icon={AssistentIcon} title={t("home.personalAssistant") || "Personal Assistant"} text={isMobile ? (t("home.personalAssistantDesc") || "Gain insights and make data-driven decisions with advanced tools.") : null} to="/assistant" />
                {/* 5. Moderation (условно) */}
                {org?.organization?.moderatorExist && user?.moderatorOrg ? (
                  <FeatureTile icon={ModerationIcon} title={t("home.moderation") || "Moderation"} text={isMobile ? (t("home.moderationDesc") || "Streamline communication and foster teamwork with efficient tools.") : null} to="/moderation" />
                ) : (
                  <FeatureTile icon={ModerationIcon} title={t("home.moderation") || "Moderation"} text={isMobile ? (t("home.moderationDesc") || "Streamline communication and foster teamwork with efficient tools.") : null} disabled />
                )}
                {/* 6. Analytics (заглушка) */}
                <FeatureTile icon={profileIcon} title={t("home.analytics") || "Analytics"} text={isMobile ? (t("home.analyticsDesc") || "Scale effortlessly with reliable and flexible infrastructure.") : null} to="/analytics" />
              </div>
              {/* Удалены nth-child стили для симметрии */}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

function FeatureTile({ icon, title, text, to, disabled, iconSize = "md" }) {
  const baseClasses = "relative isolate flex flex-col items-center text-center h-full min-h-[200px] sm:min-h-[220px] md:min-h-[240px] rounded-2xl p-4 md:p-5 bg-white border border-gray-100 transition-all duration-300 ease-out group-hover:border-violet-300 group-hover:shadow-[0_10px_28px_-8px_rgba(109,40,217,0.35)]";
  const disabledClasses = disabled ? " opacity-50 pointer-events-none" : "";
  const iconWrapperSize = iconSize === "sm" ? "h-16 w-16" : "h-20 w-20";
  const iconImgSize = iconSize === "sm" ? "h-12 w-12" : "h-16 w-16";
  const card = (
    <div className={baseClasses + disabledClasses}>
      {/* Облако */}
      <div className="pointer-events-none absolute left-1/2 top-11 -translate-x-1/2 -translate-y-1/2 h-32 w-32 rounded-full blur-3xl opacity-80 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.5)_0%,rgba(124,58,237,0)_70%)] transition-opacity duration-500 group-hover:opacity-0" />
      {icon && (
        <div className={`relative flex items-center justify-center ${iconWrapperSize}`}>
          <img
            src={icon}
            alt=""
            role="presentation"
            className={`relative ${iconImgSize} object-contain select-none transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:scale-110 drop-shadow-[0_8px_22px_rgba(124,58,237,0.35)]`}
            loading="lazy"
            decoding="async"
          />
        </div>
      )}
      <h3 className="mt-2 text-lg font-semibold tracking-tight transition-colors duration-300 group-hover:text-violet-700">{title}</h3>
      {text ? (
        <p className="mt-2 max-w-[34ch] text-sm text-gray-500 leading-relaxed">{text}</p>
      ) : null}
      <div className="mt-auto pt-2" />
    </div>
  );
  if (disabled || !to) {
    return <div className="group relative h-full">{card}</div>;
  }
  return (
    <Link
      to={to}
      className="group outline-none focus-visible:ring-2 focus-visible:ring-violet-500/50 rounded-2xl block h-full transition-transform duration-300 ease-out hover:-translate-y-1 hover:scale-[1.02]"
    >
      {card}
    </Link>
  );
}

function BackgroundDecor() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-1/3 -left-1/3 h-[80vh] w-[80vw] rounded-full blur-3xl opacity-[.18] bg-[radial-gradient(closest-side,theme(colors.violet.500),transparent_65%)]" />
      <div className="absolute -bottom-1/3 -right-1/3 h-[80vh] w-[80vw] rounded-full blur-3xl opacity-[.18] bg-[radial-gradient(closest-side,theme(colors.blue.500),transparent_65%)]" />
      <div className="absolute inset-0 mix-blend-overlay opacity-[.08] [background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.8%22 numOctaves=%222%22 stitchTiles=%22stitch%22/></filter><rect width=%2240%22 height=%2240%22 filter=%22url(%23n)%22 opacity=%220.8%22/></svg>')]" />
    </div>
  );
}
